import React, { useRef, useState } from 'react';

import { useSnackbar } from 'notistack';

import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';
import TableChartOutlinedIcon from '@mui/icons-material/TableChartOutlined';
import { IconButton, ListItemIcon, ListItemText, Menu, MenuItem, Tooltip } from '@mui/material';

import { useExportLensPdf } from '~/hooks/apis/reporting/useExportLensPdf';

import { downloadLensCsv } from '~/utils/downloadLensCsv';

import type { LensState } from './types';

type LensDownloadProps = {
  lensId: string;
  title: string;
  state: LensState;
  blocks: Array<string>;
  tabs?: Array<string>;
};

export default function LensDownload({ lensId, title, state, blocks, tabs }: LensDownloadProps) {
  const anchor = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  const [csvPending, setCsvPending] = useState(false);
  const exportPdf = useExportLensPdf(lensId);
  const { enqueueSnackbar } = useSnackbar();
  const pending = exportPdf.isPending || csvPending;
  const pdf = () => {
    setOpen(false);
    exportPdf.mutate({ state, title });
  };
  const csv = () => {
    setOpen(false);
    setCsvPending(true);
    downloadLensCsv(lensId, { state, blocks, tabs })
      .catch(() => enqueueSnackbar('The CSV didn’t download. Try again.', { variant: 'warning' }))
      .finally(() => setCsvPending(false));
  };

  return (
    <>
      <Tooltip
        title={
          pending ? 'Preparing the download…' : exportPdf.isError ? 'The PDF could not be made. Try again.' : 'Download'
        }
      >
        <span>
          <IconButton
            aria-haspopup="menu"
            aria-label="Download this Lens"
            disabled={pending}
            onClick={() => setOpen(true)}
            ref={anchor}
            size="small"
          >
            <FileDownloadOutlinedIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
      <Menu anchorEl={anchor.current} onClose={() => setOpen(false)} open={open}>
        <MenuItem onClick={pdf}>
          <ListItemIcon>
            <PictureAsPdfOutlinedIcon fontSize="small" />
          </ListItemIcon>
          PDF
        </MenuItem>
        {blocks.length ? (
          <MenuItem onClick={csv}>
            <ListItemIcon>
              <TableChartOutlinedIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="CSV" secondary={blocks.length > 1 ? `${blocks.length} blocks, zipped` : undefined} />
          </MenuItem>
        ) : null}
      </Menu>
    </>
  );
}
